import { createStore } from "zustand";
import { apiService } from "@/services/api.service";
import { storageService } from "@/services/storage.service";
import { API } from "@/constants/api";
import { persist } from "zustand/middleware";

/**
 * Marketplace configuration interface
 * Structured to match the expected API response
 */
export interface MarketplaceConfig {
  id: string;
  name: string;
  // Branding assets
  favicon?: string;
  logo?: string;
  web_header_logo?: string;
  webapp_logo?: string;
  background_image?: string;
  marketplace_user_id?: number;

  
  // Theme colors
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    background?: string;
    text?: string;
    btn_color?: string;
  };
  
  // Feature flags and other settings
  settings: {
    features?: Record<string, boolean>;
    [key: string]: any;
  };
  
  // Additional properties
  [key: string]: any;
}

/**
 * Cache key for marketplace configuration in IndexedDB
 */
const MARKETPLACE_CONFIG_KEY = "marketplace-config";

/**
 * Default cache expiration time (1 hour)
 */
const DEFAULT_CACHE_EXPIRATION = 60 * 60 * 1000;

/**
 * Marketplace state interface
 * Follows the same pattern as existing stores
 */
export interface MarketplaceState {
  config: MarketplaceConfig | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: number | null;
  isInitialized: boolean;
  fetchConfig: (bypassCache?: boolean, forceDirect?: boolean) => Promise<void>;
  clearConfig: () => void;
  getConfigItem: <T>(path: string, defaultValue: T) => T;
  hydrateFromSSR: (initialData: MarketplaceConfig) => void;
  initialize: () => Promise<void>;
  location: LocationState;  // Update this type
}

// Add a new interface for location state
interface LocationState {
  lat: number | null;
  lng: number | null;
  address: string;
}

// Add a new type for store actions
type MarketplaceActions = {
  setLocation: (lat: number, lng: number, address: string) => void;
  clearLocation: () => void;
  // ... other actions
};

// Add initial state
const initialState: MarketplaceState = {
  config: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
  isInitialized: false,
  location: {
    lat: null,
    lng: null,
    address: '',
  },
  fetchConfig: async (bypassCache = false, forceDirect = false) => {
    // Implement the logic for fetching the config
    console.log('Fetching config with', { bypassCache, forceDirect });
  },
  clearConfig: () => {
    // Implement the logic for clearing the config
    console.log('Clearing config');
  },
  getConfigItem: <T>(path: string, defaultValue: T): T => {
    // Implement the logic for retrieving a config item
    console.log('Getting config item for path:', path);
    return defaultValue;
  },
  hydrateFromSSR: (initialData: MarketplaceConfig) => {
    // Implement the logic for hydrating from SSR
    console.log('Hydrating from SSR with data:', initialData);
  },
  initialize: async () => {
    // Implement the logic for initialization
    console.log('Initializing marketplace state');
  }
};

let marketplaceStore: ReturnType<typeof createMarketplaceStore> | null = null;

/**
 * Deep merges two objects, preferring target values over source when both exist
 */
const deepMerge = (source: any, target: any): any => {
  const output = { ...source };
  
  if (isObject(source) && isObject(target)) {
    Object.keys(target).forEach(key => {
      if (isObject(target[key])) {
        if (!(key in source)) {
          output[key] = target[key];
        } else {
          output[key] = deepMerge(source[key], target[key]);
        }
      } else {
        output[key] = target[key];
      }
    });
  }
  
  return output;
};

const isObject = (item: any): boolean => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

/**
 * Merges API response data with mock data for missing fields
 * @param apiData - Data received from the API
 * @returns Enhanced data with mock values for missing fields
 */
const enhanceWithMockDataIfNeeded = async (apiData: MarketplaceConfig | null): Promise<MarketplaceConfig> => {
  if (!apiData) {
    console.log('%c[Marketplace] API returned no data, using complete mock data', 'background: #FF9800; color: white; padding: 3px; border-radius: 2px;');
    
    // Fetch complete mock data
    const mockResponse = await fetch('/api/mock-marketplace', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ marketplace_reference_id: API.MARKETPLACE_REFERENCE_ID })
    });
    
    if (!mockResponse.ok) {
      throw new Error('Failed to fetch mock data');
    }
    
    const mockData = await mockResponse.json();
    return mockData.data;
  }
  
  // Check if any essential fields are missing
  const hasMissingFields = !apiData.colors?.primary || 
                          !apiData.settings?.features ||
                          !apiData.background_image;
  
  if (!hasMissingFields) {
    console.log('%c[Marketplace] API data is complete, no need to enhance', 'background: #4CAF50; color: white; padding: 3px; border-radius: 2px;');
    return apiData;
  }
  
  console.log('%c[Marketplace] API data is incomplete, enhancing with mock data', 'background: #FF9800; color: white; padding: 3px; border-radius: 2px;');
  
  // Fetch mock data to fill in the gaps
  const mockResponse = await fetch('/api/mock-marketplace', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ marketplace_reference_id: API.MARKETPLACE_REFERENCE_ID })
  });
  
  if (!mockResponse.ok) {
    console.warn('Could not fetch mock data for enhancement, returning partial API data');
    return apiData;
  }
  
  const mockData = await mockResponse.json();
  const enhancedData = deepMerge(mockData.data, apiData);
  
  console.log('%c[Marketplace] Data enhanced with mock values for missing fields', 'background: #4CAF50; color: white; padding: 3px; border-radius: 2px;');
  return enhancedData;
};

/**
 * Creates the marketplace store
 * Uses API service and storage service to manage data
 */
const createMarketplaceStore = () => {
  return createStore<MarketplaceState & MarketplaceActions>()(
    persist(
      (set, get) => ({
        ...initialState,
        /**
         * Fetches the marketplace configuration
         * Checks local cache first, then falls back to API
         * @param bypassCache - If true, skip cache check and force API call (default: false)
         * @param forceDirect - If true, bypass mock API and call real API directly (default: false)
         */
        fetchConfig: async (bypassCache = false, forceDirect = false) => {
          set({ isLoading: true, error: null });
          
          try {
            const cacheKey = `${MARKETPLACE_CONFIG_KEY}-${API.MARKETPLACE_REFERENCE_ID}`;
            console.log('%c[Marketplace] Checking cache with key:', 'background: #673AB7; color: white; padding: 3px; border-radius: 2px;', cacheKey);
            
            // Only check cache if we're not bypassing it
            if (!bypassCache) {
              const isValid = await storageService.isValid(cacheKey, DEFAULT_CACHE_EXPIRATION);
              
              if (isValid) {
                const cachedData = await storageService.getItem<MarketplaceConfig>(cacheKey);
                if (cachedData) {
                  console.log('%c[Marketplace] Using cached data', 'background: #4CAF50; color: white; padding: 3px; border-radius: 2px;');
                  set({
                    config: cachedData.data,
                    lastUpdated: cachedData.timestamp,
                    isLoading: false,
                    isInitialized: true,
                  });
                  return;
                }
              }
            }
            
            // Cache is invalid or being bypassed, fetch from API
            console.log('%c[Marketplace] Fetching from API', 'background: #FF9800; color: white; padding: 3px; border-radius: 2px;');
            
            // Call API service, with forceDirect parameter to bypass mock API if needed
            const response = await apiService.fetchMarketplaceConfiguration<MarketplaceConfig>(forceDirect);
            
            if (!response || !response.data) {
              console.error('[Marketplace] API returned no data');
              throw new Error('API returned no data');
            }
            
            // Enhance data with mock data if needed
            const enhancedData = await enhanceWithMockDataIfNeeded(response.data);
            
            // Store the enhanced data in IndexedDB
            try {
              await storageService.setItem(cacheKey, enhancedData);
              console.log('[Marketplace] Successfully cached marketplace data');
            } catch (cacheError) {
              console.error('[Marketplace] Failed to cache data:', cacheError);
              // Continue even if caching fails - we still have the data in memory
            }
            
            // Update store state
            set({
              config: enhancedData,
              lastUpdated: Date.now(),
              isLoading: false,
              isInitialized: true,
            });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : "Failed to fetch configuration",
              isLoading: false,
            });
          }
        },
        
        /**
         * Hydrates the store with data from server-side rendering
         * @param initialData Data fetched during SSR
         */
        hydrateFromSSR: (initialData: MarketplaceConfig) => {
          if (!initialData) return;
          
          console.log('%c[Marketplace] Hydrating store from SSR data', 'background: #3F51B5; color: white; padding: 3px; border-radius: 2px;');
          
          set({
            config: initialData,
            lastUpdated: Date.now(),
            isLoading: false,
            isInitialized: true,
            error: null
          });
          
          // Also cache the data for future use
          const cacheKey = `${MARKETPLACE_CONFIG_KEY}-${API.MARKETPLACE_REFERENCE_ID}`;
          storageService.setItem(cacheKey, initialData).catch(err => {
            console.error('[Marketplace] Failed to cache SSR data:', err);
          });
        },
        
        /**
         * Initializes the store from client-side
         * Checks cache first, then falls back to API
         */
        initialize: async () => {
          // Skip if already initialized or currently loading
          if (get().isInitialized || get().isLoading) {
            console.log('%c[Marketplace] Store already initialized or loading, skipping', 'background: #3F51B5; color: white; padding: 3px; border-radius: 2px;');
            return;
          }
          
          set({ isLoading: true, error: null });
          console.log('%c[Marketplace] Initializing store from client-side', 'background: #3F51B5; color: white; padding: 3px; border-radius: 2px;');
          
          try {
            const cacheKey = `${MARKETPLACE_CONFIG_KEY}-${API.MARKETPLACE_REFERENCE_ID}`;
            
            // Check if we have valid cached data
            const isValid = await storageService.isValid(cacheKey, DEFAULT_CACHE_EXPIRATION);
            
            if (isValid) {
              // Use cached data
              console.log('%c[Marketplace] Valid cache found, using cached data', 'background: #3F51B5; color: white; padding: 3px; border-radius: 2px;');
              const cachedData = await storageService.getItem<MarketplaceConfig>(cacheKey);
              
              if (cachedData) {
                set({
                  config: cachedData.data,
                  lastUpdated: cachedData.timestamp,
                  isLoading: false,
                  isInitialized: true,
                  error: null
                });
                return;
              }
            }
            
            // No valid cache, fetch from API
            console.log('%c[Marketplace] No valid cache, fetching from API', 'background: #3F51B5; color: white; padding: 3px; border-radius: 2px;');
            await get().fetchConfig(true);
            set({ isInitialized: true });
          } catch (error) {
            set({
              error: error instanceof Error ? error.message : "Failed to initialize marketplace store",
              isLoading: false
            });
          }
        },
        
        /**
         * Clears the configuration from state and cache
         */
        clearConfig: async () => {
          const cacheKey = `${MARKETPLACE_CONFIG_KEY}-${API.MARKETPLACE_REFERENCE_ID}`;
          console.log(`%c[Marketplace] Clearing cache for key:`, "background: #FF5722; color: white; padding: 3px; border-radius: 2px;", cacheKey);
          
          try {
            await storageService.removeItem(cacheKey);
            console.log(`%c[Marketplace] Cache cleared successfully`, "background: #FF5722; color: white; padding: 3px; border-radius: 2px;");
          } catch (error) {
            console.error("[Marketplace] Error clearing cache:", error);
          }
          
          set({
            config: null,
            lastUpdated: null,
            isInitialized: false,
          });
          
          console.log(`%c[Marketplace] State reset successfully`, "background: #FF5722; color: white; padding: 3px; border-radius: 2px;");
        },
        
        /**
         * Utility function to get a nested configuration item using a dot path
         * e.g. getConfigItem("settings.theme.primaryColor", "#default")
         */
        getConfigItem: <T>(path: string, defaultValue: T): T => {
          const config = get().config;
          if (!config) return defaultValue;
          
          const parts = path.split(".");
          let current: any = config;
          
          for (const part of parts) {
            if (current === undefined || current === null) {
              return defaultValue;
            }
            current = current[part];
          }
          
          return current !== undefined && current !== null ? current : defaultValue;
        },
        setLocation: (lat: number, lng: number, address: string) => {
          // console.log("setLocation called:", lat, lng, address);
          set((state) => ({
            ...state,
            location: {
              lat,
              lng,
              address
            }
          }));
        },

        clearLocation: () => {
          set((state) => ({
            ...state,
            location: initialState.location
          }));
        },
      }),
      {
        name: 'marketplace-storage',
        partialize: (state) => ({
          config: state.config,
          location: state.location,
          lastUpdated: state.lastUpdated,
          isInitialized: state.isInitialized,
        }),
      }
    )
  );
};

// Create a single store instance
marketplaceStore = createMarketplaceStore();

/**
 * Safe client-side hook for Next.js
 * Returns a dummy store during SSR to avoid hydration issues
 */
export const useMarketplaceStore = () => {
  // For SSR, return a simple object with basic props that implements all methods of MarketplaceState
  if (typeof window === "undefined") {
    console.log('[useMarketplaceStore] Creating SSR dummy store');
    
    const dummyStore: MarketplaceState & MarketplaceActions = {
      ...initialState,
      fetchConfig: async (bypassCache = false) => {
        console.log('[SSR] dummy fetchConfig called with bypassCache:', bypassCache);
      },
      clearConfig: () => {
        console.log('[SSR] dummy clearConfig called');
      },
      getConfigItem: <T>(path: string, defaultValue: T): T => {
        console.log('[SSR] dummy getConfigItem called:', path);
        return defaultValue;
      },
      hydrateFromSSR: (initialData) => {
        console.log('[SSR] dummy hydrateFromSSR called');
      },
      initialize: async () => {
        console.log('[SSR] dummy initialize called');
      },
      setLocation: (lat, lng, address) => {
        console.log('[SSR] dummy setLocation called:', lat, lng, address);
      },
      clearLocation: () => {
        console.log('[SSR] dummy clearLocation called');
      }
      
    };
    return dummyStore;
  }
  
  // Ensure store exists when used client-side
  if (!marketplaceStore) {
    console.log('[useMarketplaceStore] Creating new marketplace store');
    marketplaceStore = createMarketplaceStore();
  } else {
    console.log('[useMarketplaceStore] Using existing marketplace store');
  }
  
  // Log all available store methods for debugging
  if (process.env.NODE_ENV === 'development') {
    const methods = Object.keys(marketplaceStore.getState()).filter(key => {
      return typeof marketplaceStore?.getState()[key as keyof (MarketplaceState & MarketplaceActions)] === 'function';

    });
    console.log('[useMarketplaceStore] Available store methods:', methods);
  }
  
  // return marketplaceStore.getState();
  return {
    ...marketplaceStore.getState(),
    location: marketplaceStore.getState().location,
  };

  
};
